/**
 * 总览（原 ControlPanel + Dashboard 合并）。
 *
 * 阶段4 信息架构收敛：4标签 → 3标签。本页作为唯一首页「总览」，承接：
 * - API Key 鉴权引导（原 ControlPanel）
 * - 推送总开关 + 手动触发 + 系统状态（原 ControlPanel）
 * - 订阅股票实时行情卡片 + 最近推送（原 Dashboard）
 *
 * 订阅 CRUD（添加/编辑/删除）统一收口到「订阅中心」；推送历史完整时间轴收口到「情报流」。
 * 本页只保留最近推送 8 条作为概览。
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap, KeyRound, RefreshCw, ArrowRight, Bell, TrendingUp,
  Loader2, CheckCircle2, XCircle, AlertTriangle, Calendar, Layers, Globe2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtNum, fmtTime, fmtDate } from "@/lib/format";
import { isTradingDay } from "@/lib/trading";
import StatusRow from "@/components/StatusRow";
import {
  SettingsApi, SubscriptionsApi, PushApi, HealthApi, StockApi,
  getApiKey, setApiKey, clearApiKey,
  type GlobalSettings, type Subscription, type PushHistoryItem,
  type PushStats, type HealthStatus, type StockQuoteResult, ApiError,
} from "@/services/api";

// 订阅股票的实时行情卡片（行情由父组件批量拉取后传入，本卡片不再自请）
function SubscribedStockCard({
  code, name, mkt,
  quote, status, onRetry,
}: {
  code: string; name: string; mkt: string;
  quote: StockQuoteResult | null;
  status: "loading" | "ok" | "error";
  onRetry: () => void;
}) {
  const nav = useNavigate();

  if (status === "loading") {
    return (
      <div className="card p-4">
        <div className="skeleton h-4 w-20 mb-3 rounded" />
        <div className="skeleton h-6 w-24 mb-2 rounded" />
        <div className="skeleton h-3 w-16 rounded" />
      </div>
    );
  }

  // 行情获取失败时也要渲染卡片（显示股票名 + 失败提示 + 重试），
  // 否则失败的股票会消失，表现为「总有一只不显示」。
  if (status === "error" || !quote) {
    return (
      <div className="card p-4">
        <button onClick={() => nav(`/stock/${code}?mkt=${mkt}`)} className="text-left">
          <div className="text-sm text-ink-100 hover:text-gold transition-colors">{name}</div>
          <div className="num text-[0.6875rem] text-ink-500">{code} · {mkt}</div>
        </button>
        <div className="mt-3 pt-3 border-t border-ink-700 flex items-center justify-between">
          <span className="text-[0.6875rem] text-ink-500 flex items-center gap-1">
            <AlertTriangle size={12} /> 行情获取失败
          </span>
          <button
            onClick={onRetry}
            className="text-[0.6875rem] text-gold hover:text-gold-soft flex items-center gap-1"
          >
            <RefreshCw size={12} /> 重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-hover p-4">
      <div className="flex items-start justify-between mb-2">
        <button onClick={() => nav(`/stock/${quote.code}?mkt=${mkt}`)} className="text-left">
          <div className="text-sm text-ink-100 hover:text-gold transition-colors">{quote.name}</div>
          <div className="num text-[0.6875rem] text-ink-500">{quote.code} · {quote.market}</div>
        </button>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="num text-xl text-ink-100">{fmtNum(quote.price)}</div>
          <div className={cn("num text-xs", quote.changePct >= 0 ? "text-bull" : "text-bear")}>
            {quote.change >= 0 ? "+" : ""}{quote.change.toFixed(2)} · {quote.changePct >= 0 ? "+" : ""}{quote.changePct.toFixed(2)}%
          </div>
        </div>
        <span className="text-[0.625rem] text-ink-500">{quote.currency}</span>
      </div>
      {quote.sector && (
        <div className="mt-3 pt-3 border-t border-ink-700 text-[0.6875rem] text-ink-400">
          所属行业：<span className="text-sky-300">{quote.sector}</span>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const isTrading = isTradingDay();

  // ----- 鉴权状态 -----
  const [authState, setAuthState] = useState<"unknown" | "open" | "required">("unknown");
  const [apiKeyInput, setApiKeyInput] = useState(getApiKey());

  // ----- 业务数据（鉴权通过后才加载）-----
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [history, setHistory] = useState<PushHistoryItem[]>([]);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 订阅股票的实时行情：{ code: StockQuoteResult }，由批量接口一次拉取
  const [quotesMap, setQuotesMap] = useState<Record<string, StockQuoteResult>>({});
  const [quotesFailed, setQuotesFailed] = useState<Set<string>>(new Set());
  const [quotesLoading, setQuotesLoading] = useState(false);

  // ----- 推送开关 / 触发 -----
  const [togglingGlobal, setTogglingGlobal] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<PushStats | null>(null);
  const [triggerError, setTriggerError] = useState("");

  const authenticated = authState === "open" || (authState === "required" && !!getApiKey());

  const refreshAll = useCallback(async () => {
    if (!authenticated) { setLoading(false); return; }
    setError("");
    try {
      const [s, subList, hist, h] = await Promise.all([
        SettingsApi.get(),
        SubscriptionsApi.list(),
        PushApi.history(7),
        HealthApi.check(),
      ]);
      setSettings(s);
      setSubs(subList);
      setHistory(hist);
      setHealth(h);
      // 批量拉行情（一次请求拿全部订阅股，降低偶发失败）
      if (subList.length) {
        setQuotesLoading(true);
        try {
          const { quotes, failed } = await StockApi.quotes(
            subList.map((x) => ({ code: x.stockCode, mkt: x.mktNum, name: x.stockName })),
          );
          const map: Record<string, StockQuoteResult> = {};
          for (const q of quotes) map[q.code] = q;
          setQuotesMap(map);
          setQuotesFailed(new Set(failed));
        } catch {
          // 批量接口失败不影响主页面加载，个股卡片自行降级
          setQuotesMap({});
          setQuotesFailed(new Set(subList.map((x) => x.stockCode)));
        } finally {
          setQuotesLoading(false);
        }
      } else {
        setQuotesMap({});
        setQuotesFailed(new Set());
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "加载失败";
      setError(msg);
      if (e instanceof ApiError && e.status === 401) setAuthState("required");
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  // 单股重试：失败时重新走批量接口刷新该股行情
  const retryQuote = useCallback(async (code: string) => {
    const sub = subs.find((s) => s.stockCode === code);
    if (!sub) return;
    try {
      const q = await StockApi.quote(code, sub.mktNum, sub.stockName);
      setQuotesMap((m) => ({ ...m, [code]: q }));
      setQuotesFailed((s) => { const n = new Set(s); n.delete(code); return n; });
    } catch { /* 重试失败保持原状 */ }
  }, [subs]);

  // 首次挂载：探测后端是否需要鉴权
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await HealthApi.check();
        if (!cancelled) { setAuthState("open"); setLoading(true); }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) setAuthState("required");
        else setAuthState("open");
        setLoading(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (authState !== "unknown") refreshAll();
  }, [authState, refreshAll]);

  // ----- API Key -----
  const saveKey = () => {
    const k = apiKeyInput.trim();
    if (!k) return;
    setApiKey(k);
    setLoading(true);
    refreshAll();
  };
  const removeKey = () => {
    clearApiKey();
    setApiKeyInput("");
    setSettings(null); setSubs([]); setHistory([]); setHealth(null);
    setAuthState("required");
  };

  // ----- 推送开关 / 触发 -----
  const toggleGlobal = async () => {
    if (!settings) return;
    setTogglingGlobal(true);
    try {
      const updated = await SettingsApi.update(!settings.pushEnabled);
      setSettings(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "切换失败");
    } finally {
      setTogglingGlobal(false);
    }
  };
  const triggerPush = async () => {
    setTriggering(true);
    setTriggerError("");
    setTriggerResult(null);
    try {
      const stats = await PushApi.trigger(true);
      setTriggerResult(stats);
      const hist = await PushApi.history(7);
      setHistory(hist);
    } catch (e) {
      setTriggerError(e instanceof ApiError ? e.message : "触发失败");
    } finally {
      setTriggering(false);
    }
  };

  // ============ 渲染 ============
  if (authState === "unknown") {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-gold" />
      </div>
    );
  }

  // 后端需要鉴权但尚未填 Key
  if (authState === "required" && !getApiKey()) {
    return (
      <div className="max-w-md mx-auto mt-16">
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={20} className="text-gold" />
            <h2 className="font-serif text-lg text-ink-100">配置 API Key</h2>
          </div>
          <p className="text-sm text-ink-400 mb-4 leading-relaxed">
            控制面板需要后端 API Key 鉴权。请在后端 <code className="text-gold">.env</code> 中设置
            <code className="text-gold"> API_KEY</code>，然后填入下方。
          </p>
          <input
            type="password"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveKey()}
            placeholder="粘贴 API Key"
            className="w-full bg-ink-900 border border-ink-700 focus:border-gold/60 px-3 py-2 text-sm text-ink-100 outline-none mb-3"
          />
          <button onClick={saveKey} disabled={!apiKeyInput.trim()} className="btn-gold w-full text-sm">
            连接
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-gold" />
      </div>
    );
  }

  const globalOff = settings && !settings.pushEnabled;
  const enabledCount = subs.filter((s) => s.enabled).length;
  const recentPushes = history.slice(0, 8);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto">
      {error && (
        <div className="flex items-center gap-2 p-3 border border-bear/40 bg-bear/10 text-bear text-sm">
          <AlertTriangle size={14} />{error}
          <button onClick={() => setError("")} className="ml-auto text-bear/60 hover:text-bear">×</button>
        </div>
      )}

      {/* Hero + 推送总开关 */}
      <section className={cn("relative grain card p-6 md:p-8 overflow-hidden animate-riseIn", globalOff && "opacity-80")}>
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/5 blur-3xl pointer-events-none" />
        <div className="absolute top-0 left-0 w-1 h-full bg-gold" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="label-tag text-gold border-gold/50 bg-gold/10">
                <Zap size={10} /> 股讯推送终端
              </span>
              <span className={cn(
                "label-tag",
                isTrading ? "text-bull border-bull/50 bg-bull/10" : "text-ink-400 border-ink-600 bg-ink-800",
              )}>
                {isTrading ? "交易日" : "非交易日"}
              </span>
              {health && (
                <span className="label-tag text-gold border-gold/40 bg-gold/10">
                  {health.mergePushDefault ? "合并推送" : "逐股推送"}
                </span>
              )}
            </div>
            <h1 className="font-serif text-2xl md:text-4xl text-ink-100 leading-tight mb-1">
              一手股市情报<span className="text-gold">，按交易节奏</span>推送
            </h1>
            <p className="text-sm text-ink-400 max-w-2xl leading-relaxed">
              订阅 <span className="num text-gold">{subs.length}</span> 只股票（启用 {enabledCount}），
              经 DeepSeek 翻译分析、相似去重后通过 pushplus 推送到微信。
            </p>
          </div>

          {/* 推送总开关 */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <div className="text-sm text-ink-100">推送总开关</div>
              <div className="text-[0.6875rem] text-ink-400">
                {globalOff ? "已暂停 · 手动仍可用" : "运行中"}
              </div>
            </div>
            <button
              onClick={toggleGlobal}
              disabled={togglingGlobal || !settings}
              className={cn(
                "w-11 h-6 rounded-full transition-colors flex items-center px-0.5",
                globalOff ? "bg-ink-700 justify-start" : "bg-gold justify-end",
                (togglingGlobal || !settings) && "opacity-50 cursor-not-allowed",
              )}
            >
              {togglingGlobal
                ? <Loader2 size={16} className="animate-spin text-ink-300" />
                : <span className={cn(
                    "w-5 h-5 rounded-full bg-ink-950 transition-all",
                    globalOff ? "" : "mr-0",
                  )} />}
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* 左：订阅行情 + 最近推送 */}
        <div className="xl:col-span-2 space-y-6">
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 title-bar">
                <Bell size={16} className="text-gold" />
                我的订阅
                <span className="num text-xs text-ink-500">({subs.length})</span>
              </h2>
              <button onClick={() => nav("/subscription")} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1">
                管理订阅 <ArrowRight size={12} />
              </button>
            </div>
            {subs.length === 0 ? (
              <div className="card p-6">
                <div className="text-sm text-ink-300 mb-2">尚未订阅任何股票</div>
                <div className="text-xs text-ink-500 mb-3">
                  前往订阅中心，输入股票代码或名称（如 600519、茅台、AAPL）添加。
                </div>
                <button onClick={() => nav("/subscription")} className="btn-gold text-xs">
                  去订阅中心添加
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {subs.map((s) => {
                  const status: "loading" | "ok" | "error" = quotesFailed.has(s.stockCode)
                    ? "error"
                    : quotesMap[s.stockCode]
                      ? "ok"
                      : quotesLoading
                        ? "loading"
                        : "error";
                  return (
                    <SubscribedStockCard
                      key={s.stockCode}
                      code={s.stockCode} name={s.stockName} mkt={s.mktNum}
                      quote={quotesMap[s.stockCode] ?? null}
                      status={status}
                      onRetry={() => retryQuote(s.stockCode)}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* 最近推送（完整历史在情报流） */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 title-bar">
                <TrendingUp size={16} className="text-gold" />
                最近推送
              </h2>
              <button onClick={() => nav("/feed")} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1">
                查看全部 <ArrowRight size={12} />
              </button>
            </div>
            <div className="space-y-3">
              {recentPushes.length === 0 ? (
                <div className="card p-6 text-center text-sm text-ink-400">
                  暂无推送记录 · 可在右侧手动触发
                </div>
              ) : (
                recentPushes.map((h, i) => {
                  // 历史记录是「曾经推送过」的留痕，取消订阅不会删除历史。
                  // 合并推送的 stockCode 是逗号拼接的多只股票，需拆分后判断：
                  // 仅当该条目涉及的所有股票都已不在订阅列表时才标「已取消订阅」。
                  const codes = h.stockCode.split(",").map((c) => c.trim()).filter(Boolean);
                  const unsubscribed = codes.length > 0
                    && codes.every((c) => !subs.some((s) => s.stockCode === c));
                  return (
                  <div key={i} className="card card-hover p-3 flex items-center gap-3">
                    <span className={cn("w-2 h-2 rounded-full shrink-0", h.success ? "bg-gold" : "bg-bear")} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="num text-xs text-ink-400">{fmtTime(h.time)}</span>
                        <span className="text-xs text-ink-200 truncate">{h.stockName}</span>
                        {h.success && (
                          <span className="label-tag text-bull border-bull/40 bg-bull/10">{h.newsCount}条</span>
                        )}
                        {unsubscribed && (
                          <span className="label-tag text-ink-400 border-ink-600 bg-ink-800/60">已取消订阅</span>
                        )}
                      </div>
                      <div className="text-xs text-ink-500 truncate mt-0.5">{h.title}</div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        {/* 右：手动触发 + 系统状态 */}
        <div className="space-y-6">
          <section className="card p-5">
            <h2 className="font-serif text-base text-ink-100 flex items-center gap-2 mb-3 title-bar">
              <Zap size={15} className="text-gold" /> 手动推送
            </h2>
            <button onClick={triggerPush} disabled={triggering} className="btn-gold w-full text-sm mb-3">
              {triggering ? (
                <><Loader2 size={14} className="animate-spin mr-1.5" />推送中...</>
              ) : "立即推送"}
            </button>
            {triggerResult && (
              <div className="text-xs space-y-1 border-t border-ink-700 pt-3">
                <div className="flex justify-between">
                  <span className="text-ink-400">推送</span>
                  <span className="text-bull num">{triggerResult.pushed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-400">跳过</span>
                  <span className="text-ink-300 num">{triggerResult.skipped}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-400">失败</span>
                  <span className="text-bear num">{triggerResult.failed}</span>
                </div>
                {triggerResult.details.map((d, i) => (
                  <div key={i} className="flex items-center gap-1.5 pt-1">
                    {d.ok
                      ? <CheckCircle2 size={11} className="text-bull shrink-0" />
                      : <XCircle size={11} className="text-bear shrink-0" />}
                    <span className="text-ink-300 truncate">{d.stock}</span>
                    <span className="text-ink-500 ml-auto num">{d.count}条</span>
                  </div>
                ))}
              </div>
            )}
            {triggerError && (
              <div className="text-xs text-bear border-t border-ink-700 pt-3">{triggerError}</div>
            )}
          </section>

          <section className="card p-5">
            <h2 className="font-serif text-base text-ink-100 flex items-center gap-2 mb-4 title-bar">
              <Calendar size={15} className="text-gold" /> 系统状态
            </h2>
            <div className="text-xs space-y-2.5">
              <StatusRow label="交易日" value={isTrading ? "是" : "否"} />
              <StatusRow label="存储模式" value={health?.storageMode || "—"} />
              <StatusRow label="DeepSeek 分析" ok={health?.deepseekEnabled} />
              <StatusRow label="Finnhub 国外源" ok={health?.finnhubEnabled} />
              <StatusRow label="试运行模式" ok={health?.dryRun} okText="开" badText="关" reverse />
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink-700">
              <span className="text-[0.6875rem] text-ink-500 flex items-center gap-1">
                <KeyRound size={11} />
                {authState === "open" ? "后端未启用鉴权" : "API Key 已配置"}
              </span>
              {authState === "required" && (
                <button onClick={removeKey} className="text-[0.6875rem] text-ink-500 hover:text-bear">断开</button>
              )}
            </div>
          </section>

          <section className="card p-5">
            <h2 className="font-serif text-base text-ink-100 flex items-center gap-2 mb-3 title-bar">
              <Layers size={15} className="text-gold" /> 推送机制
            </h2>
            <div className="space-y-2.5 text-xs text-ink-400 leading-relaxed">
              <p>· 腾讯云 SCF Timer 定时触发</p>
              <p>· 合并推送：增量资讯聚合为 <span className="text-gold">1 条</span> 消息</p>
              <p>· 三道去重：hash → 增量窗口 → 标题相似度</p>
              <p>· DeepSeek 翻译 + 走势影响分析</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
